/**
 * Datasource local Services: abstração + implementação localforage.
 */

import localforage from 'localforage';
import { Service } from '@/core/domain';

const CACHE_KEY_PREFIX = 'retornaar:services';

export interface ServiceLocalDataSource {
  getById(id: string): Promise<Service | null>;
  getAllByUserId(userId: string): Promise<Service[]>;
  getAll(userId: string): Promise<Service[]>;
  /** Substitui o cache do usuário pelo snapshot remoto (getAll). */
  cacheAll(userId: string, services: Service[]): Promise<void>;
  create(service: Service): Promise<Service>;
  update(id: string, service: Partial<Service>): Promise<Service>;
  delete(id: string): Promise<void>;
  clear(userId?: string): Promise<void>;
}

const store = localforage.createInstance({ name: 'retornaar', storeName: 'services' });

export class ServiceLocalForageDataSource implements ServiceLocalDataSource {
  private cacheKey(userId: string) {
    return `${CACHE_KEY_PREFIX}:${userId}`;
  }

  private async getMap(userId: string): Promise<Record<string, Service>> {
    const raw = await store.getItem<Record<string, Service>>(this.cacheKey(userId));
    return raw ?? {};
  }

  private async setMap(userId: string, map: Record<string, Service>): Promise<void> {
    await store.setItem(this.cacheKey(userId), map);
  }

  async getById(id: string): Promise<Service | null> {
    const keys = await store.keys();
    const serviceKeys = keys.filter((k) => typeof k === 'string' && k.startsWith(CACHE_KEY_PREFIX));
    for (const key of serviceKeys) {
      const map = await store.getItem<Record<string, Service>>(key);
      if (map?.[id]) return map[id];
    }
    return null;
  }

  async getAllByUserId(userId: string): Promise<Service[]> {
    const map = await this.getMap(userId);
    return Object.values(map);
  }

  async getAll(userId: string): Promise<Service[]> {
    return this.getAllByUserId(userId);
  }

  async cacheAll(userId: string, services: Service[]): Promise<void> {
    const map: Record<string, Service> = {};
    for (const s of services) {
      map[s.id] = s;
    }
    await this.setMap(userId, map);
  }

  async create(service: Service): Promise<Service> {
    const map = await this.getMap(service.user_id);
    map[service.id] = service;
    await this.setMap(service.user_id, map);
    return service;
  }

  async update(id: string, service: Partial<Service>): Promise<Service> {
    const s = await this.getById(id);
    if (!s) throw new Error('Service not found');
    const map = await this.getMap(s.user_id);
    const current = map[id];
    if (!current) throw new Error('Service not found');
    const updated = { ...current, ...service, id };
    map[id] = updated;
    await this.setMap(s.user_id, map);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const s = await this.getById(id);
    if (!s) return;
    const map = await this.getMap(s.user_id);
    delete map[id];
    await this.setMap(s.user_id, map);
  }

  async clear(userId?: string): Promise<void> {
    if (userId) {
      await store.removeItem(this.cacheKey(userId));
    } else {
      const keys = await store.keys();
      for (const key of keys) {
        if (typeof key === 'string' && key.startsWith(CACHE_KEY_PREFIX)) {
          await store.removeItem(key);
        }
      }
    }
  }
}
